<script setup lang="ts">
import { ActionButton, Select } from '@taler/ui'
import { formatLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ReportBucket } from '../internal/report-range.ts'
import type { ReportRangeQueryState } from '../composables/use-report-range-query-state.ts'

const props = defineProps<{ state: ReportRangeQueryState }>()

const { t, locale } = useI18n()
const today = computed(() => formatLocalDate(todayUtc(systemClock)))
const from = computed(() => formatLocalDate(props.state.range.value.from))
const until = computed(() => formatLocalDate(props.state.range.value.until))

const BUCKET_LABEL_KEYS: Record<ReportBucket, string> = {
  DAY: 'reporting.bucketDaily',
  WEEK: 'reporting.bucketWeekly',
  MONTH: 'reporting.bucketMonthly',
  YEAR: 'reporting.bucketYearly',
}
const bucketOptions = computed(() =>
  props.state.offeredBuckets.value.map((value) => ({ value, label: t(BUCKET_LABEL_KEYS[value]) })),
)

const DATE_INPUT =
  'w-[7.5rem] bg-transparent text-[length:var(--text-step-2)] tabular-nums text-[color:var(--color-text)] outline-none'
</script>

<template>
  <div class="ml-auto flex flex-wrap items-center gap-[var(--space-inline)] py-[var(--space-inline)]">
    <div
      class="flex h-[var(--density-control)] items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border bg-[color:var(--color-surface)] pl-[var(--space-cell)] pr-[var(--space-inline)]"
      :class="
        state.isInverted.value
          ? 'border-[color:var(--color-danger)]'
          : 'border-[color:var(--color-border-control)]'
      "
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        class="shrink-0 text-[color:var(--color-text-muted)]"
      >
        <rect x="2" y="3" width="12" height="11" rx="2" />
        <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" />
      </svg>
      <input
        type="date"
        :lang="locale"
        :max="today"
        :value="from"
        :aria-label="t('reporting.from')"
        :aria-invalid="state.isInverted.value || undefined"
        :class="DATE_INPUT"
        @change="state.setFrom(($event.target as HTMLInputElement).value)"
      />
      <span aria-hidden="true" class="text-[color:var(--color-text-muted)]">–</span>
      <input
        type="date"
        :lang="locale"
        :value="until"
        :aria-label="t('reporting.until')"
        :class="DATE_INPUT"
        @change="state.setUntil(($event.target as HTMLInputElement).value)"
      />
    </div>
    <div class="flex items-center gap-1">
      <ActionButton variant="secondary" :aria-label="t('reporting.rangeEarlier')" @click="state.step(-1)">
        <span aria-hidden="true">‹</span>
      </ActionButton>
      <ActionButton variant="secondary" :aria-label="t('reporting.rangeLater')" @click="state.step(1)">
        <span aria-hidden="true">›</span>
      </ActionButton>
    </div>
    <Select
      :model-value="state.range.value.bucket"
      :options="bucketOptions"
      :aria-label="t('reporting.interval')"
      @update:model-value="state.setBucket($event as ReportBucket)"
    />
  </div>
</template>
